import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { User } from "@supabase/supabase-js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Settings,
  Award,
  LogOut,
  Menu,
  Users,
  MessageSquare,
  BookOpen,
  Video,
  Rocket,
  LayoutDashboard,
  GraduationCap,
  ChevronDown,
  Compass,
  Shield,
  BookMarked,
  Lightbulb,
  BadgeCheck,
  Building2,
  ShoppingCart,
  Calendar,
  User as UserIcon,
  Heart,
  Diamond,
  Wrench,
} from "lucide-react";
import maipLogo from "@/assets/maip-logo.png";
import { BedarfsAnalysatorDialog } from "@/components/BedarfsAnalysatorDialog";
import LanguageSwitcher from "@/components/LanguageSwitcher";

// =============================================================================
// MEGA-MENU ITEM COMPONENT
// =============================================================================
interface MenuItemProps {
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  badge?: string;
  badgeColor?: string;
  onClick?: () => void;
  highlight?: boolean;
  subline?: string;
}

const MegaMenuItem: React.FC<MenuItemProps> = ({
  title,
  description,
  href,
  icon,
  badge,
  badgeColor = "bg-primary",
  onClick,
  highlight,
  subline,
}) => (
  <Link
    to={href}
    onClick={onClick}
    className={`group flex items-start gap-3 p-3 rounded-lg transition-colors ${
      highlight
        ? "bg-premium-muted/60 border border-premium/20 hover:bg-premium-muted"
        : "hover:bg-muted/50"
    }`}
  >
    <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
      highlight
        ? "bg-premium/15 group-hover:bg-premium/25"
        : "bg-muted group-hover:bg-primary/10"
    }`}>
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className={`font-semibold transition-colors ${
          highlight ? "text-foreground group-hover:text-premium-foreground" : "text-foreground group-hover:text-primary"
        }`}>
          {title}
        </span>
        {badge && (
          <span
            className={`px-1.5 py-0.5 text-[10px] font-bold ${badgeColor} text-primary-foreground rounded-full`}
          >
            {badge}
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground line-clamp-2 mt-0.5">
        {description}
      </p>
      {subline && (
        <p className="text-xs text-muted-foreground/70 mt-1.5">
          {subline}
        </p>
      )}
    </div>
  </Link>
);

// =============================================================================
// MAIN HEADER COMPONENT
// =============================================================================
const Header = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isExpert, setIsExpert] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBedarfsAnalysator, setShowBedarfsAnalysator] = useState(false);
  const [academyMenuOpen, setAcademyMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Cached query for next seminar – shared queryKey with DualPathSelector
  const { data: nextSeminar } = useQuery({
    queryKey: ["next-leadership-seminar"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("seminars")
        .select("id, title, start_date, max_participants, current_participants, location")
        .eq("status", "published")
        .gte("start_date", new Date().toISOString())
        .order("start_date", { ascending: true })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 min cache
  });

  const seminarSubline = nextSeminar
    ? `Nächster Termin: ${new Date(nextSeminar.start_date).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })} in ${nextSeminar.location} – ${nextSeminar.max_participants - nextSeminar.current_participants <= 5 ? "Fast ausgebucht!" : `Noch ${nextSeminar.max_participants - nextSeminar.current_participants} Plätze`}`
    : undefined;

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkExpertRole(session.user.id);
        checkAdminRole(session.user.id);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        checkExpertRole(session.user.id);
        checkAdminRole(session.user.id);
      } else {
        setIsExpert(false);
        setIsAdmin(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const checkExpertRole = async (userId: string) => {
    try {
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .in("role", ["experte", "admin"]);
      setIsExpert(roles && roles.length > 0);
    } catch (error) {
      console.error("Error checking expert role:", error);
      setIsExpert(false);
    }
  };

  const checkAdminRole = async (userId: string) => {
    try {
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin");
      setIsAdmin(roles && roles.length > 0);
    } catch (error) {
      console.error("Error checking admin role:", error);
      setIsAdmin(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const closeAcademyMenu = () => setAcademyMenuOpen(false);

  return (
    <header className="bg-background shadow-sm sticky top-0 z-50 border-b">
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img src={maipLogo} alt="MAIP.info Logo" className="h-[40px]" />
          </Link>

          {/* ============================================================= */}
          {/* DESKTOP NAVIGATION - SaaS Standard: Marketing links, User rechts */}
          {/* ============================================================= */}
          <nav aria-label="Hauptnavigation" className="hidden lg:flex items-center gap-1 flex-1 justify-center">
            
            {/* 1. Lösungen */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1 px-4 py-2 text-foreground hover:text-primary hover:bg-muted/50 rounded-lg transition-colors font-medium whitespace-nowrap">
                  Lösungen
                  <ChevronDown className="w-4 h-4 opacity-50" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64 bg-background z-50">
                <DropdownMenuItem asChild>
                  <Link to="/loesungen/kliniken" className="cursor-pointer flex items-start gap-3 py-3">
                    <Building2 className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <div className="font-medium">Für Kliniken & MedTech</div>
                      <div className="text-xs text-muted-foreground">Prozess-Effizienz als Überlebensstrategie</div>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/loesungen/pflegeheime" className="cursor-pointer flex items-start gap-3 py-3">
                    <Building2 className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <div className="font-medium">Für Pflegeheime</div>
                      <div className="text-xs text-muted-foreground">Wiederbelegung statt Aufnahmestopp</div>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/loesungen/pflegedienste" className="cursor-pointer flex items-start gap-3 py-3">
                    <Building2 className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <div className="font-medium">Für Pflegedienste</div>
                      <div className="text-xs text-muted-foreground">Doku ohne Nacharbeit</div>
                    </div>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/cultural-onboarding" className="cursor-pointer flex items-start gap-3 py-3">
                    <GraduationCap className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        Cultural Onboarding
                        <span className="px-1.5 py-0.5 text-[10px] font-bold bg-emerald-600 text-white rounded-full">NEU</span>
                      </div>
                      <div className="text-xs text-muted-foreground">Werte-Transfer für internationale Fachkräfte</div>
                    </div>
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* 2. Akademie - MEGA MENU (now includes Live-Seminare) */}
            <Popover open={academyMenuOpen} onOpenChange={setAcademyMenuOpen}>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-1 px-4 py-2 text-foreground hover:text-primary hover:bg-muted/50 rounded-lg transition-colors font-medium whitespace-nowrap">
                  Akademie
                  <ChevronDown className="w-4 h-4 opacity-50" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                className="w-[800px] p-0 bg-background border shadow-xl rounded-xl"
                align="center"
                sideOffset={8}
              >
                <div className="p-6">
                  <div className="grid grid-cols-3 gap-6">
                    {/* Column 1: Lernpfade */}
                    <div className="space-y-1">
                      <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest px-3 mb-3">
                        Lernpfade
                      </h4>
                      <MegaMenuItem
                        title="KI-Pilot"
                        description="Operative Exzellenz in 7 Tagen."
                        href="/ki-pilot"
                        icon={<Rocket className="w-5 h-5 text-primary" />}
                        badge="Express"
                        badgeColor="bg-primary"
                        onClick={closeAcademyMenu}
                      />
                      <MegaMenuItem
                        title="KI-Lotse"
                        description="Multiplikatoren-Ausbildung (14 Tage)."
                        href="/ki-lotse"
                        icon={<Compass className="w-5 h-5 text-primary" />}
                        badge="Zertifikat"
                        badgeColor="bg-primary"
                        onClick={closeAcademyMenu}
                      />
                      <MegaMenuItem
                        title="MAV-KI-Lotse"
                        description="Rechtssichere Mitbestimmung für Gremien."
                        href="/ki-lotse"
                        icon={<Shield className="w-5 h-5 text-primary" />}
                        badge="Zertifikat"
                        badgeColor="bg-primary"
                        onClick={closeAcademyMenu}
                      />
                      <MegaMenuItem
                        title="Cultural Onboarding"
                        description="Werte-Transfer für internationale Fachkräfte."
                        href="/cultural-onboarding"
                        icon={<Heart className="w-5 h-5 text-primary" />}
                        badge="Neu"
                        badgeColor="bg-primary"
                        onClick={closeAcademyMenu}
                      />
                      <div className="pt-2 px-3">
                        <Link
                          to="/lotsen"
                          className="text-sm text-primary hover:underline font-medium"
                          onClick={closeAcademyMenu}
                        >
                          → Alle Tracks vergleichen
                        </Link>
                      </div>
                    </div>

                    {/* Column 2: Executive & Präsenz */}
                    <div className="space-y-1">
                      <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest px-3 mb-3">
                        Executive & Präsenz
                      </h4>
                      <MegaMenuItem
                        title="MAIP Executive (3 Tage)"
                        description="Strategie-Intensivkurs für Führungskräfte."
                        href={nextSeminar ? `/seminare/${nextSeminar.id}` : "/seminare"}
                        icon={<Calendar className="w-5 h-5 text-premium" />}
                        badge="Premium"
                        badgeColor="bg-premium"
                        onClick={closeAcademyMenu}
                        highlight
                        subline={seminarSubline}
                      />
                      <MegaMenuItem
                        title="Inhouse-Lösungen"
                        description="Maßgeschneiderte Team-Schulungen vor Ort."
                        href="/kontakt"
                        icon={<Wrench className="w-5 h-5 text-primary" />}
                        onClick={closeAcademyMenu}
                      />
                      {isAdmin && (
                        <MegaMenuItem
                          title="Master-Audit"
                          description="Verbundweite Steuerung für Träger."
                          href="/master-audit"
                          icon={<Building2 className="w-5 h-5 text-primary" />}
                          onClick={closeAcademyMenu}
                        />
                      )}
                      <div className="pt-3 px-3 border-t mt-3">
                        <Link
                          to="/preise"
                          className="group flex items-center gap-2 text-sm font-semibold text-premium hover:underline"
                          onClick={closeAcademyMenu}
                        >
                          <Diamond className="w-4 h-4" />
                          MAIP Premium Pass →
                        </Link>
                      </div>
                    </div>

                    {/* Column 3: Ressourcen & Governance */}
                    <div className="space-y-1">
                      <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest px-3 mb-3">
                        Ressourcen & Governance
                      </h4>
                      <MegaMenuItem
                        title="Programm-Übersicht"
                        description="Das vollständige MAIP-Curriculum."
                        href="/programm"
                        icon={<BookMarked className="w-5 h-5 text-primary" />}
                        onClick={closeAcademyMenu}
                      />
                      <MegaMenuItem
                        title="Wissens-Hub"
                        description="Fachartikel & News."
                        href="/wissen"
                        icon={<BookOpen className="w-5 h-5 text-primary" />}
                        onClick={closeAcademyMenu}
                      />
                      <MegaMenuItem
                        title="KI-Glossar"
                        description="Alle Fachbegriffe erklärt."
                        href="/glossar"
                        icon={<BookMarked className="w-5 h-5 text-primary" />}
                        onClick={closeAcademyMenu}
                      />
                      <MegaMenuItem
                        title="Zertifikat prüfen"
                        description="Öffentliche Echtheitsprüfung."
                        href="/verify"
                        icon={<BadgeCheck className="w-5 h-5 text-primary" />}
                        onClick={closeAcademyMenu}
                      />
                      <MegaMenuItem
                        title="Roadmap 2026"
                        description="Kommende Features."
                        href="/#pipeline"
                        icon={<Lightbulb className="w-5 h-5 text-primary" />}
                        badge="Coming Soon"
                        badgeColor="bg-muted-foreground"
                        onClick={closeAcademyMenu}
                      />
                    </div>
                  </div>

                  {/* Bottom CTA */}
                  <div className="mt-6 pt-4 border-t flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => {
                          setShowBedarfsAnalysator(true);
                          closeAcademyMenu();
                        }}
                        className="text-sm font-semibold text-primary hover:underline"
                      >
                        🎯 Welches Modul passt zu mir?
                      </button>
                      <Link
                        to="/wissenschaft-methodik"
                        className="text-sm text-muted-foreground hover:text-foreground"
                        onClick={closeAcademyMenu}
                      >
                        Wissenschaft & Methodik
                      </Link>
                    </div>
                    <Link
                      to="/preise"
                      className="text-sm font-medium text-muted-foreground hover:text-foreground"
                      onClick={closeAcademyMenu}
                    >
                      Preise & Förderung →
                    </Link>
                  </div>
                </div>
              </PopoverContent>
            </Popover>

            {/* 3. Ressourcen (ehemals Über MAIP + Community) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1 px-4 py-2 text-foreground hover:text-primary hover:bg-muted/50 rounded-lg transition-colors font-medium whitespace-nowrap">
                  Ressourcen
                  <ChevronDown className="w-4 h-4 opacity-50" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 bg-background z-50">
                <DropdownMenuItem asChild>
                  <Link to="/warum-maip" className="cursor-pointer">
                    Warum MAIP?
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/dozierende-team" className="cursor-pointer">
                    Dozierende & Team
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/gestaltungsrat" className="cursor-pointer">
                    Gestaltungsrat
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/faq" className="cursor-pointer">
                    FAQ
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/kontakt" className="cursor-pointer">
                    Kontakt
                  </Link>
                </DropdownMenuItem>
                {isExpert && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/experten-bereich/forum" className="cursor-pointer flex items-center">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Forum
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/experten-bereich/ressourcen" className="cursor-pointer flex items-center">
                        <BookOpen className="w-4 h-4 mr-2" />
                        Experten-Ressourcen
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/experten-bereich/webinare" className="cursor-pointer flex items-center">
                        <Video className="w-4 h-4 mr-2" />
                        Webinare
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* 4. Preise - Direct Link */}
            <Link
              to="/preise"
              className="px-4 py-2 text-foreground hover:text-primary hover:bg-muted/50 rounded-lg transition-colors font-medium whitespace-nowrap"
            >
              Preise
            </Link>
          </nav>

          {/* ============================================================= */}
          {/* DESKTOP: Right Side - CTA + Language + User                    */}
          {/* ============================================================= */}
          <div className="hidden lg:flex items-center gap-2">
            {/* CTA */}
            <Link to="/bestellen">
              <Button size="sm">
                <ShoppingCart className="w-4 h-4 mr-1.5" />
                Lizenzen kaufen
              </Button>
            </Link>

            {/* Language Switcher */}
            <LanguageSwitcher variant="compact" />

            {/* User Menu */}
            {isAdmin ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <UserIcon className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 bg-background z-50">
                  <DropdownMenuItem asChild>
                    <Link to="/cockpit" className="cursor-pointer flex items-center">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Mein Cockpit
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/experten-bereich" className="cursor-pointer flex items-center">
                      <Award className="w-4 h-4 mr-2" />
                      Mein Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="cursor-pointer flex items-center">
                      <Settings className="w-4 h-4 mr-2" />
                      Admin-Bereich
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/admin/content" className="cursor-pointer flex items-center">
                      <Settings className="w-4 h-4 mr-2" />
                      Inhalte verwalten
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : user && isExpert ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <UserIcon className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 bg-background z-50">
                  <DropdownMenuItem asChild>
                    <Link to="/cockpit" className="cursor-pointer flex items-center">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Mein Cockpit
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/experten-bereich" className="cursor-pointer flex items-center">
                      <Award className="w-4 h-4 mr-2" />
                      Mein Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full">
                    <UserIcon className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 bg-background z-50">
                  <DropdownMenuItem asChild>
                    <Link to="/cockpit" className="cursor-pointer flex items-center">
                      <LayoutDashboard className="w-4 h-4 mr-2" />
                      Mein Cockpit
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </div>

          {/* ============================================================= */}
          {/* MOBILE: Hamburger Menu                                        */}
          {/* ============================================================= */}
          <div className="flex lg:hidden items-center gap-3">
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Settings className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-background z-50">
                  <DropdownMenuItem asChild>
                    <Link to="/admin" className="cursor-pointer flex items-center">
                      <Settings className="w-4 h-4 mr-2" />
                      Admin-Bereich
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/experten-bereich" className="cursor-pointer flex items-center">
                      <Award className="w-4 h-4 mr-2" />
                      Mein Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[320px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-primary" />
                    Navigation
                  </SheetTitle>
                </SheetHeader>

                <nav aria-label="Mobile Navigation" className="flex flex-col gap-2 mt-6">
                  {/* Mein Cockpit - Prominent */}
                  {user && (
                    <Link
                      to="/cockpit"
                      className="flex items-center gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg text-foreground hover:bg-primary/10 transition-colors font-medium"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <LayoutDashboard className="w-5 h-5 text-primary" />
                      Mein Cockpit
                    </Link>
                  )}

                  {/* Karriere-Pfade */}
                  <div className="pt-4 pb-2 text-xs font-bold text-muted-foreground uppercase tracking-widest">
                    Karriere-Pfade
                  </div>
                  <Link
                    to="/ki-pilot"
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Rocket className="w-5 h-5 text-blue-500" />
                    <div>
                      <span className="font-medium">KI-Pilot</span>
                      <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-slate-400 text-white rounded-full">🥈</span>
                    </div>
                  </Link>
                  <Link
                    to="/ki-lotse"
                    className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Compass className="w-5 h-5 text-amber-500" />
                    <div>
                      <span className="font-medium">KI-Lotse</span>
                      <span className="ml-2 px-1.5 py-0.5 text-[10px] font-bold bg-amber-500 text-white rounded-full">🥇</span>
                    </div>
                  </Link>

                  {/* Akademie & Seminare */}
                  <div className="pt-4 pb-2 text-xs font-bold text-muted-foreground uppercase tracking-widest border-t">
                    Akademie
                  </div>
                  <Link to="/seminare" className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                    <Calendar className="w-5 h-5 text-primary" />
                    <span className="font-medium">Live-Seminare</span>
                  </Link>
                  <Link to="/programm" className="text-foreground hover:text-primary transition-colors py-2 pl-3" onClick={() => setMobileMenuOpen(false)}>
                    Programm-Übersicht
                  </Link>
                  <Link to="/wissen" className="text-foreground hover:text-primary transition-colors py-2 pl-3" onClick={() => setMobileMenuOpen(false)}>
                    Wissens-Hub
                  </Link>
                  <Link to="/glossar" className="text-foreground hover:text-primary transition-colors py-2 pl-3" onClick={() => setMobileMenuOpen(false)}>
                    KI-Glossar
                  </Link>
                  <Link to="/grundlagen" className="text-foreground hover:text-primary transition-colors py-2 pl-3" onClick={() => setMobileMenuOpen(false)}>
                    KI-Grundlagen
                  </Link>
                  <Link to="/datenschutz-kompass" className="text-foreground hover:text-primary transition-colors py-2 pl-3" onClick={() => setMobileMenuOpen(false)}>
                    Datenschutz-Kompass
                  </Link>
                  <Link to="/mitbestimmung-info" className="text-foreground hover:text-primary transition-colors py-2 pl-3" onClick={() => setMobileMenuOpen(false)}>
                    KI & Mitbestimmung
                  </Link>

                  {/* Lösungen */}
                  <div className="pt-4 pb-2 text-xs font-bold text-muted-foreground uppercase tracking-widest border-t">
                    Lösungen
                  </div>
                  <Link to="/loesungen/kliniken" className="text-foreground hover:text-primary transition-colors py-2 pl-3" onClick={() => setMobileMenuOpen(false)}>
                    Für Kliniken & MedTech
                  </Link>
                  <Link to="/loesungen/pflegeheime" className="text-foreground hover:text-primary transition-colors py-2 pl-3" onClick={() => setMobileMenuOpen(false)}>
                    Für Pflegeheime
                  </Link>
                  <Link to="/loesungen/pflegedienste" className="text-foreground hover:text-primary transition-colors py-2 pl-3" onClick={() => setMobileMenuOpen(false)}>
                    Für Pflegedienste
                  </Link>

                  {/* Ressourcen (Über MAIP) */}
                  <div className="pt-4 pb-2 text-xs font-bold text-muted-foreground uppercase tracking-widest border-t">
                    Ressourcen
                  </div>
                  <Link to="/warum-maip" className="text-foreground hover:text-primary transition-colors py-2 pl-3" onClick={() => setMobileMenuOpen(false)}>
                    Warum MAIP?
                  </Link>
                  <Link to="/dozierende-team" className="text-foreground hover:text-primary transition-colors py-2 pl-3" onClick={() => setMobileMenuOpen(false)}>
                    Dozierende & Team
                  </Link>
                  <Link to="/gestaltungsrat" className="text-foreground hover:text-primary transition-colors py-2 pl-3" onClick={() => setMobileMenuOpen(false)}>
                    Gestaltungsrat
                  </Link>

                  {/* Service */}
                  <div className="pt-4 pb-2 text-xs font-bold text-muted-foreground uppercase tracking-widest border-t">
                    Service
                  </div>
                  <Link to="/preise" className="text-foreground hover:text-primary transition-colors py-2 pl-3 font-medium" onClick={() => setMobileMenuOpen(false)}>
                    Preise & Förderung
                  </Link>
                  <Link to="/faq" className="text-foreground hover:text-primary transition-colors py-2 pl-3" onClick={() => setMobileMenuOpen(false)}>
                    FAQ
                  </Link>
                  <Link to="/kontakt" className="text-foreground hover:text-primary transition-colors py-2 pl-3" onClick={() => setMobileMenuOpen(false)}>
                    Kontakt
                  </Link>
                  <Link to="/verify" className="text-foreground hover:text-primary transition-colors py-2 pl-3" onClick={() => setMobileMenuOpen(false)}>
                    Zertifikat prüfen
                  </Link>

                  {/* Bedarfs-Analysator CTA */}
                  <button
                    onClick={() => {
                      setShowBedarfsAnalysator(true);
                      setMobileMenuOpen(false);
                    }}
                    className="mt-4 w-full p-3 bg-primary text-primary-foreground rounded-lg font-semibold text-center"
                  >
                    🎯 Welches Modul passt zu mir?
                  </button>

                  {/* Community - Only for Experts */}
                  {isExpert && (
                    <>
                      <div className="pt-4 pb-2 text-xs font-bold text-muted-foreground uppercase tracking-widest border-t">
                        Community
                      </div>
                      <Link to="/experten-bereich/forum" className="text-foreground hover:text-primary transition-colors py-2 pl-3 flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                        <MessageSquare className="w-4 h-4" />
                        Forum
                      </Link>
                      <Link to="/experten-bereich/ressourcen" className="text-foreground hover:text-primary transition-colors py-2 pl-3 flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                        <BookOpen className="w-4 h-4" />
                        Ressourcen
                      </Link>
                      <Link to="/experten-bereich/webinare" className="text-foreground hover:text-primary transition-colors py-2 pl-3 flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                        <Video className="w-4 h-4" />
                        Webinare
                      </Link>
                      <Link to="/experten-bereich" className="text-foreground hover:text-primary transition-colors py-2 pl-3 flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                        <Award className="w-4 h-4" />
                        Dashboard
                      </Link>
                    </>
                  )}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <BedarfsAnalysatorDialog
        open={showBedarfsAnalysator}
        onOpenChange={setShowBedarfsAnalysator}
      />
    </header>
  );
};

export default Header;
